import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { trackFunnelEvent } from '../lib/funnel-tracker';
import PublicWhatsappInfoBanner from '../components/PublicWhatsappInfoBanner';
import VisitCounterBand from '../components/VisitCounterBand';
import {
    GraduationCap, ChevronRight, Brain, Compass, Layers, BarChart3,
    Sparkles, Shield, Target, Database, BookOpen, MessageSquare, Lock,
    Baby, Heart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PILARES_ESTRATEGICOS = [
    { icon: Database, titulo: 'Evidencia real CPEIP/MINEDUC', texto: 'Trabaja con los 18 indicadores oficiales del Portafolio 2026 y los porcentajes nacionales reales de desempeño.' },
    { icon: Layers, titulo: 'Clusters pedagógicos', texto: 'Identifica indicadores tractores y zonas de alto desgaste a partir de análisis de correlaciones.' },
    { icon: Compass, titulo: 'Estrategia personalizada', texto: 'Ruta Inteligente, Estrategia Mixta (Recomendación Acontista Educa) o Alto Rendimiento. Tú eliges, el sistema se adapta.' },
    { icon: Brain, titulo: 'IA pedagógica', texto: 'Acompañamiento por perfil docente (Generalista, Básica o Media) con lenguaje oficial de las rúbricas.' },
    { icon: BarChart3, titulo: 'Dificultad y tiempo estimado', texto: 'Cada indicador muestra su dificultad nacional y carga estimada, para que priorices con criterio.' },
    { icon: Shield, titulo: 'Cuida tu bienestar docente', texto: 'No promovemos perfección absoluta. Apuntamos a coherencia pedagógica y sostenibilidad real.' },
];

const MODULOS = [
    { titulo: 'Módulo 1 · Planificación, evaluación y reflexión', indicadores: '7 indicadores oficiales' },
    { titulo: 'Módulo 2 · Clase grabada', indicadores: '7 indicadores oficiales por perfil docente' },
    { titulo: 'Módulo 3 · Trabajo colaborativo y desarrollo profesional', indicadores: '4 indicadores · obligatorios + voluntarios' },
];

const NUMEROS = [
    { valor: '18', etiqueta: 'Indicadores oficiales' },
    { valor: '3', etiqueta: 'Módulos del Portafolio' },
    { valor: '4', etiqueta: 'Clusters pedagógicos' },
    { valor: '5', etiqueta: 'Estrategias inteligentes' },
];

export default function Landing() {
    const [docentesActivos, setDocentesActivos] = useState(null);

    useEffect(() => {
        let alive = true;
        fetch(`${API}/api/public/stats`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive) setDocentesActivos(d?.docentes_activos ?? null); })
            .catch(() => { /* silencioso: la landing nunca debe romperse por esto */ });
        return () => { alive = false; };
    }, []);

    const socialProofLabel = (() => {
        if (docentesActivos === null) return null;
        if (docentesActivos >= 100) {
            return `Más de ${docentesActivos.toLocaleString('es-CL')} docentes ya construyen su estrategia de Portafolio.`;
        }
        if (docentesActivos >= 10) {
            return 'Docentes de todo Chile ya utilizan Portafolio Digital Docente.';
        }
        return null;
    })();

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 lg:px-8">
                    <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
                        <img
                            src="https://customer-assets.emergentagent.com/job_101ec70a-ddc1-481b-9653-c6adf0debcdb/artifacts/fl22sgx2_logo%20acontista%20educa.png"
                            alt="Acontista Educa"
                            className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
                            width="56"
                            height="56"
                            loading="eager"
                            decoding="async"
                            data-testid="acontista-logo"
                        />
                        <div className="leading-tight">
                            <span className="block font-heading text-base font-bold text-slate-900">Portafolio Digital Docente</span>
                            <span className="block text-[10px] font-medium uppercase tracking-[0.12em] text-violet-700">Acontista Educa</span>
                            <a
                                href="mailto:acontistaeduca@gmail.com"
                                className="hidden sm:inline-block mt-0.5 text-[11px] text-slate-500 hover:text-slate-700 transition-colors"
                                data-testid="acontista-email"
                            >
                                acontistaeduca@gmail.com
                            </a>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/login">
                            <Button variant="ghost" data-testid="login-link">Iniciar sesión</Button>
                        </Link>
                        <Link to="/register" onClick={() => trackFunnelEvent('register_click', { last_action: 'landing_hero' })}>
                            <Button data-testid="register-link">Crear cuenta</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero — banner dominante full-width sobre azul profundo */}
            <section
                className="relative overflow-hidden"
                style={{
                    background:
                        'linear-gradient(135deg, #071428 0%, #0b1f3a 35%, #12345b 70%, #1b4d8a 100%)',
                }}
            >
                {/* glow decorativo sutil */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
                    style={{
                        background:
                            'radial-gradient(closest-side, rgba(59,130,246,0.35), transparent 70%)',
                    }}
                />
                {/* grid noise opcional */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                        backgroundSize: '22px 22px',
                    }}
                />

                <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-10 lg:px-8 lg:pb-24 lg:pt-14">
                    {/* pill superior */}
                    <div className="flex justify-center">
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            Portafolio Docente 2026 · Estrategia con datos reales
                        </span>
                    </div>

                    {/* Banner imagen FULL WIDTH */}
                    <div className="mt-8 lg:mt-10">
                        <div className="relative mx-auto w-full overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-[0_30px_80px_-30px_rgba(2,8,23,0.7)]">
                            {/* Toda la imagen es clicable → Login / Registro existente */}
                            <Link
                                to="/login"
                                aria-label="Comienza ahora · Iniciar sesión o crear cuenta"
                                data-testid="hero-banner-cta"
                                onClick={() => trackFunnelEvent('register_click', { last_action: 'landing_hero_banner' })}
                                className="block focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300/90"
                            >
                                <img
                                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_101ec70a-ddc1-481b-9653-c6adf0debcdb/artifacts/eab3d62b7d84633e_Portafolio%20Docente%202026_%20Maximiza%20Tu%20Tiempo%20%281%29.png"
                                    alt="Portafolio Docente 2026 · Sin vueltas, con poco tiempo: maximiza tu tiempo. Tú redactas, la IA evalúa con las rúbricas oficiales CPEIP 2026 y te propone una versión Competente o Destacada"
                                    className="block h-auto w-full"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                    width="1536"
                                    height="1024"
                                    data-testid="hero-banner-image"
                                />
                            </Link>
                            {/* overlay sutil que ata la imagen al fondo azul */}
                            <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                style={{
                                    background:
                                        'linear-gradient(180deg, rgba(7,20,40,0) 70%, rgba(7,20,40,0.18) 100%)',
                                }}
                            />
                        </div>
                    </div>

                    {/* CTA real visible en móvil (el del banner queda muy pequeño al escalar) */}
                    <div className="mt-4 flex justify-center lg:hidden">
                        <Link
                            to="/empezar"
                            onClick={() => trackFunnelEvent('register_click', { last_action: 'landing_hero_banner_mobile' })}
                            data-testid="hero-banner-cta-mobile"
                            className="inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-900/30 transition-colors hover:bg-emerald-500"
                        >
                            <Sparkles className="h-4 w-4" />
                            MEJORAR AHORA MI PRIMERA TAREA
                            <ChevronRight className="h-4 w-4" />
                        </Link>
                    </div>

                    {/* Contador público de visitas (sesiones) — justo bajo el banner */}
                    <VisitCounterBand />

                    {/* Grupo informativo público WhatsApp (potenciales usuarios) */}
                    <PublicWhatsappInfoBanner />

                    {/* Texto principal debajo del banner */}
                    <div className="mx-auto mt-10 max-w-3xl text-center lg:mt-14">
                        <h1
                            className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl leading-[1.1]"
                            data-testid="hero-headline"
                        >
                            Tú escribes. La IA te ayuda a llevar tu{' '}
                            <span className="bg-gradient-to-r from-emerald-300 to-sky-300 bg-clip-text text-transparent">
                                redacción
                            </span>{' '}
                            más lejos.
                        </h1>

                        <p
                            className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-200/90 lg:text-lg"
                            data-testid="hero-subheadline"
                        >
                            <strong className="font-semibold text-white">
                                Perfecciona la redacción de tus tareas
                            </strong>{' '}
                            con propuestas orientadas a Competente y Destacado, basadas exclusivamente
                            en las rúbricas oficiales del CPEIP 2026.
                        </p>

                        {/* Diferenciadores en chips claros */}
                        <ul className="mt-8 flex flex-wrap items-center justify-center gap-2">
                            {[
                                'Propuestas Competente y Destacado',
                                'Rúbricas oficiales CPEIP 2026',
                                'IA cerrada: no inventa evidencia',
                                '3 mejoras gratuitas para empezar',
                            ].map((item) => (
                                <li
                                    key={item}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur"
                                >
                                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-400/90 text-slate-900">
                                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-2.5 w-2.5">
                                            <path
                                                fillRule="evenodd"
                                                d="M16.7 5.3a1 1 0 010 1.4l-7.4 7.4a1 1 0 01-1.4 0L3.3 9.5a1 1 0 011.4-1.4L8.6 12l6.7-6.7a1 1 0 011.4 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        {/* CTAs */}
                        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                            <Link to="/empezar" onClick={() => trackFunnelEvent('register_click', { last_action: 'landing_hero' })}>
                                <Button
                                    size="lg"
                                    className="gap-2 bg-white text-slate-900 hover:bg-slate-100 text-base shadow-lg shadow-blue-950/30"
                                    data-testid="hero-register"
                                >
                                    Mejorar ahora mi primera tarea
                                    <ChevronRight className="h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white text-base"
                                    data-testid="hero-login"
                                >
                                    Iniciar sesión
                                </Button>
                            </Link>
                        </div>

                        <p className="mt-5 inline-flex items-center gap-1.5 text-xs text-white/70">
                            <Lock className="h-3 w-3" /> Acceso protegido · tu progreso se guarda de forma privada
                        </p>
                        {socialProofLabel && (
                            <p
                                className="mt-3 text-xs italic tracking-wide text-white/70"
                                data-testid="landing-social-proof"
                            >
                                {socialProofLabel}
                            </p>
                        )}
                    </div>
                </div>

                {/* Transición suave hacia la siguiente sección */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-12"
                    style={{
                        background:
                            'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
                    }}
                />
            </section>

            {/* Franja Parvularia — captación específica para educadoras de párvulos */}
            <section className="relative overflow-hidden border-y border-pink-200 bg-gradient-to-br from-pink-50 via-white to-sky-50 py-14 sm:py-16" data-testid="parvularia-band">
                {/* Decoración pastel */}
                <div className="pointer-events-none absolute -left-16 top-6 h-40 w-40 rounded-full bg-pink-200/40 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -right-20 bottom-0 h-52 w-52 rounded-full bg-sky-200/40 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute right-8 top-8 hidden md:block" aria-hidden="true">
                    <div className="flex items-center gap-1">
                        <span className="text-4xl">🧸</span>
                        <span className="text-3xl opacity-70">🎨</span>
                        <span className="text-4xl">✨</span>
                    </div>
                </div>
                <div className="relative mx-auto max-w-6xl px-4 lg:px-8">
                    <div className="grid gap-8 md:grid-cols-[auto,1fr,auto] md:items-center">
                        {/* Ilustración pastel */}
                        <div className="flex justify-center md:justify-start">
                            <div className="relative">
                                <div className="flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-3xl bg-white border-4 border-pink-200 shadow-lg">
                                    <Baby className="h-12 w-12 sm:h-14 sm:w-14 text-pink-500" />
                                </div>
                                <span className="absolute -right-2 -bottom-2 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-sky-400 text-white shadow-md">
                                    <Heart className="h-4 w-4 fill-current" />
                                </span>
                            </div>
                        </div>

                        {/* Copy */}
                        <div className="text-center md:text-left">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-pink-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-pink-800">
                                <Sparkles className="h-3 w-3" />
                                Nuevo · Educación Parvularia 2026
                            </span>
                            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                                También estamos para <span className="text-pink-600">educadoras de párvulos</span>
                            </h2>
                            <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed max-w-2xl">
                                Portafolio Digital Docente incorpora las <strong>18 rúbricas oficiales de Educación Parvularia 2026</strong>,
                                con resultados nacionales 2025, mapa de riesgo por indicador y un asistente IA especializado exclusivamente
                                en tus tres módulos: planificación y evaluación, grabación de una experiencia, y trabajo colaborativo.
                            </p>
                            <ul className="mt-4 grid gap-1.5 text-xs sm:text-sm text-slate-700 sm:grid-cols-2 max-w-xl mx-auto md:mx-0">
                                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-500 shrink-0" />Rúbricas oficiales CPEIP 2026 completas</li>
                                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-500 shrink-0" />Análisis IA por indicador (Sala Cuna, NM, NT)</li>
                                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-500 shrink-0" />Resultados nacionales 2025 por rúbrica</li>
                                <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-pink-500 shrink-0" />Claves para Básico, Competente y Destacado</li>
                            </ul>
                        </div>

                        {/* CTA */}
                        <div className="flex flex-col items-center md:items-end gap-2">
                            <Link
                                to="/register"
                                onClick={() => trackFunnelEvent('register_click', { last_action: 'parvularia_band' })}
                                data-testid="parvularia-band-cta"
                            >
                                <Button className="h-12 px-6 gap-2 bg-pink-600 hover:bg-pink-700 shadow-lg shadow-pink-200/60 rounded-xl transition-transform hover:-translate-y-0.5">
                                    <Baby className="h-4 w-4" />
                                    Comenzar como educadora
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </Link>
                            <span className="text-[11px] text-slate-500">Al registrarte elige «Educación Parvularia»</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comunidad · Jornada realizada (compacto, alineado izquierda) */}
            <section className="bg-white" data-testid="comunidad-jornada-section">
                <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8 lg:py-14">
                    <div className="grid gap-8 md:grid-cols-12 md:items-center">
                        {/* Bloque texto/título (col izquierda) */}
                        <div className="md:col-span-5">
                            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
                                Comunidad docente en crecimiento
                            </div>
                            <h2
                                className="mt-4 font-heading text-2xl font-bold leading-tight text-slate-900 lg:text-3xl"
                                data-testid="comunidad-headline"
                            >
                                Más de <span className="text-emerald-700">100 docentes</span> ya participaron en nuestras jornadas nacionales gratuitas.
                            </h2>
                            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                Detrás de la plataforma hay una trayectoria real de acompañamiento a docentes chilenos: encuentros abiertos, análisis pedagógicos y apoyo estratégico para elaborar el Portafolio con base en evidencia CPEIP/MINEDUC.
                            </p>
                        </div>

                        {/* Imagen jornada (col derecha, menor tamaño, con sello esquinero) */}
                        <div className="md:col-span-7">
                            <a
                                href="https://www.youtube.com/watch?v=1CA3ci8F0F8"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackFunnelEvent('convocatoria_11jul_banner_click', { last_action: 'landing_comunidad_card' })}
                                className="group relative block max-w-md overflow-hidden rounded-xl ring-1 ring-slate-200 shadow-lg shadow-slate-900/10 transition-transform hover:scale-[1.01]"
                                aria-label="Jornada Nacional Gratuita del 11 de julio 2026 · Ver grabación en YouTube"
                                data-testid="jornada-card-link"
                            >
                                <img
                                    src="https://customer-assets.emergentagent.com/job_101ec70a-ddc1-481b-9653-c6adf0debcdb/artifacts/e4p2mpj6_ChatGPT%20Image%2010%20jul%202026%2C%2011_37_50%20p.m..png"
                                    alt="1ra Jornada Nacional Gratuita de Apelación al Portafolio Docente · 11 de julio 2026"
                                    className="block w-full h-auto"
                                    loading="lazy"
                                    data-testid="jornada-card-image"
                                />
                                {/* Sello esquinero: Evento realizado */}
                                <div
                                    className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-lg ring-2 ring-white/40"
                                    data-testid="jornada-evento-badge"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                    Evento realizado · 11 jul 2026
                                </div>
                                {/* Sello CTA "Ver grabación" — aparece en hover, muy sutil */}
                                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent px-3 pb-3 pt-8 opacity-0 transition-opacity group-hover:opacity-100">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold text-slate-900">
                                        ▶ Ver grabación en YouTube
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Números */}
            <section className="border-b border-slate-200 bg-slate-50/50">
                <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 px-4 py-10 sm:grid-cols-4 lg:px-8">
                    {NUMEROS.map((n) => (
                        <div key={n.etiqueta} className="text-center">
                            <div className="font-heading text-4xl font-extrabold text-slate-900">{n.valor}</div>
                            <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{n.etiqueta}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* IA & Autoría — Compromiso institucional con la Resolución Exenta 1.657 / Manual 2026 */}
            <section className="border-b border-slate-200 bg-white py-16 sm:py-20" data-testid="ia-autoria-section">
                <div className="mx-auto max-w-5xl px-4 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Uso responsable de la IA</span>
                        <h2 className="mt-3 font-heading text-3xl font-bold text-slate-900 sm:text-4xl leading-tight">
                            La Inteligencia Artificial acompaña tu juicio profesional.<br className="hidden sm:block" />
                            <span className="text-slate-500">No lo reemplaza.</span>
                        </h2>
                        <p className="mt-5 text-slate-700 leading-relaxed">
                            El Portafolio 2026 es una <strong>actividad personal</strong>: sus evidencias deben ser originales,
                            corresponder a tu práctica profesional y ser de tu exclusiva autoría (<strong>Resolución Exenta
                            N.° 1.657, 9 abr 2026 · Manual Portafolio 2026</strong>). Nuestra plataforma fue diseñada
                            precisamente para respetar ese principio.
                        </p>
                    </div>

                    {/* Cómo funciona: 6 pasos */}
                    <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
                        <h3 className="font-heading text-lg font-semibold text-slate-900">¿Cómo utiliza la IA Portafolio Digital Docente?</h3>
                        <p className="mt-1.5 text-sm text-slate-600">
                            La IA <strong>no redacta el Portafolio por ti</strong>. El proceso siempre es este:
                        </p>
                        <ol className="mt-5 grid gap-3 sm:grid-cols-2">
                            {[
                                'Tú redactas personalmente tu borrador.',
                                'Tú describes tus propias experiencias, decisiones y evidencias.',
                                'La IA analiza el borrador con las rúbricas oficiales 2026.',
                                'La IA entrega retroalimentación técnica: fortalezas, brechas y oportunidades.',
                                'Tú decides libremente qué incorporar y qué descartar.',
                                'Tú vuelves a redactar con tu experiencia profesional.',
                            ].map((step, i) => (
                                <li key={i} className="flex items-start gap-3 rounded-lg bg-white/90 border border-slate-200/80 px-3.5 py-2.5">
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-800 text-xs font-bold font-mono">{i + 1}</span>
                                    <span className="text-sm text-slate-800 leading-snug">{step}</span>
                                </li>
                            ))}
                        </ol>
                        <p className="mt-5 text-sm text-slate-600 italic">
                            La IA cumple un rol similar al de un mentor, un colega con experiencia o un corrector técnico que revisa tu borrador antes de la entrega — solo que la retroalimentación es <strong>inmediata, objetiva y alineada con los criterios oficiales</strong>.
                        </p>
                    </div>

                    {/* 4 negaciones + compromiso */}
                    <div className="mt-8 grid gap-5 md:grid-cols-3">
                        <div className="md:col-span-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50/70 p-6">
                            <h3 className="font-heading text-base font-semibold text-emerald-900">El juicio profesional siempre te pertenece</h3>
                            <ul className="mt-4 space-y-2 text-sm text-emerald-900">
                                <li className="flex items-start gap-2"><span className="mt-0.5">✅</span><span><strong>No inventa experiencias.</strong></span></li>
                                <li className="flex items-start gap-2"><span className="mt-0.5">✅</span><span><strong>No crea evidencias.</strong></span></li>
                                <li className="flex items-start gap-2"><span className="mt-0.5">✅</span><span><strong>No reemplaza la reflexión profesional.</strong></span></li>
                                <li className="flex items-start gap-2"><span className="mt-0.5">✅</span><span><strong>No genera respuestas para ser copiadas como propias.</strong></span></li>
                            </ul>
                        </div>
                        <div className="rounded-2xl bg-gradient-to-br from-indigo-800 via-indigo-900 to-slate-900 p-6 text-white shadow-lg">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-200">Nuestro compromiso</div>
                            <div className="mt-2 font-heading text-2xl font-bold leading-tight">
                                No hacer el Portafolio por ti.
                            </div>
                            <p className="mt-3 text-sm text-indigo-100 leading-relaxed">
                                Nuestro propósito es acompañarte para que seas <strong>tú</strong>, con tu experiencia y reflexión pedagógica, quien construya un Portafolio auténtico y sólido.
                            </p>
                        </div>
                    </div>

                    {/* Bloque autoría / CPEIP */}
                    <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/70 p-6 sm:p-8">
                        <h3 className="font-heading text-base font-semibold text-amber-900">Autoría y seguridad del docente</h3>
                        <p className="mt-2 text-sm text-amber-900 leading-relaxed">
                            El CPEIP implementa un sistema de revisión digital que detecta plagio parcial o total. Los portafolios que contravengan las condiciones de veracidad son declarados <strong>OBJETADOS</strong>.
                        </p>
                        <blockquote className="mt-4 border-l-4 border-amber-400 bg-white/70 pl-4 py-3 pr-4 text-xs sm:text-sm text-slate-700 italic rounded-r">
                            &quot;Como parte de la corrección de los Portafolios, se implementa un sistema de revisión digital que permite detectar situaciones de plagio parcial o total, con evidencia rendida en el proceso 2026 o con evidencia evaluada en años anteriores. En tal caso, se declarará <strong>OBJETADO</strong> el Portafolio.&quot; — <span className="not-italic font-semibold">CPEIP</span>
                        </blockquote>
                        <p className="mt-4 text-sm text-amber-900 leading-relaxed">
                            Portafolio Digital Docente fue diseñada precisamente para <strong>evitar exponerte innecesariamente</strong> a observaciones u objeciones. Nuestra función es analizar el borrador que <em>tú</em> escribes y contrastarlo con las rúbricas oficiales — nunca inventar experiencias ni evidencias por ti.
                        </p>
                    </div>

                    {/* ¿Por qué nuestra IA es diferente? · 3 fuentes oficiales */}
                    <div className="mt-16">
                        <div className="max-w-3xl">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Diferencia técnica</span>
                            <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                                ¿Por qué nuestra IA es diferente?
                            </h3>
                            <p className="mt-4 text-slate-700 leading-relaxed">
                                La calidad de una Inteligencia Artificial depende, principalmente, de la <strong>calidad de la información</strong>
                                con la que trabaja. Nuestra plataforma <strong>no utiliza modelos que busquen información libremente en Internet</strong>.
                                Hemos desarrollado un <strong>sistema cerrado de análisis pedagógico</strong>, entrenado exclusivamente con
                                fuentes oficiales y especializadas de la Evaluación Docente chilena.
                            </p>
                        </div>

                        {/* 3 fuentes oficiales */}
                        <div className="mt-8 grid gap-4 md:grid-cols-3">
                            {[
                                {
                                    tag: 'Fuente 1',
                                    title: 'Rúbricas Oficiales 2026',
                                    body: 'Rúbricas del Portafolio Docente 2026 publicadas por DocenteMás. La referencia normativa de la evaluación.',
                                    tagClass: 'bg-sky-100 text-sky-800',
                                },
                                {
                                    tag: 'Fuente 2',
                                    title: 'Marco para la Buena Enseñanza',
                                    body: 'El MBE es el referente oficial de la evaluación docente en Chile — desde aquí se derivan los criterios.',
                                    tagClass: 'bg-indigo-100 text-indigo-800',
                                },
                                {
                                    tag: 'Fuente 3',
                                    title: 'Base de datos exclusiva',
                                    body: 'Resultados reales y anonimizados de procesos de Evaluación Docente de años anteriores, analizados sistemáticamente.',
                                    tagClass: 'bg-emerald-100 text-emerald-800',
                                },
                            ].map((f, i) => (
                                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                    <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${f.tagClass}`}>
                                        <span className="font-mono">0{i + 1}</span>
                                        <span>{f.tag}</span>
                                    </div>
                                    <h4 className="mt-3 font-heading text-lg font-bold text-slate-900 leading-snug">{f.title}</h4>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
                                </div>
                            ))}
                        </div>

                        {/* Frase de valor — blockquote destacado */}
                        <figure className="mt-8 rounded-2xl bg-gradient-to-br from-indigo-900 via-slate-900 to-slate-800 p-6 sm:p-8 shadow-lg text-white">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">Nuestra propuesta de valor</div>
                            <blockquote className="mt-3 font-heading text-xl sm:text-2xl font-semibold leading-snug">
                                <span className="text-indigo-300 mr-1.5">“</span>Nuestra Inteligencia Artificial no fue entrenada para escribir Portafolios;
                                fue entrenada para <span className="text-amber-300">comprender cómo el CPEIP evalúa un Portafolio</span>.<span className="text-indigo-300 ml-1">”</span>
                            </blockquote>
                            <figcaption className="mt-4 text-xs text-indigo-200/80">— Equipo Portafolio Digital Docente</figcaption>
                        </figure>

                        {/* Evitamos las respuestas genéricas */}
                        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
                            <h4 className="font-heading text-base font-semibold text-slate-900">Evitamos las respuestas genéricas</h4>
                            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                                Las IAs abiertas pueden entregar respuestas atractivas pero <strong>incorporar información desactualizada</strong>,
                                interpretar incorrectamente las rúbricas oficiales o recomendar acciones que <em>no coinciden</em> con los criterios reales del CPEIP.
                                Nuestro propósito no es generar respuestas llamativas — es que la retroalimentación sea <strong>lo más cercana posible a los criterios oficiales</strong>.
                            </p>
                        </div>
                    </div>

                    {/* Radar Docente contextualiza */}
                    <div className="mt-10 rounded-2xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-sky-50 p-6 sm:p-8" data-testid="radar-context-block">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white text-xl">📡</div>
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-teal-800">Plataforma hermana</span>
                                <h3 className="mt-1 font-heading text-2xl font-bold text-slate-900 leading-tight">
                                    El contexto también importa
                                </h3>
                                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                                    Portafolio Digital Docente se integra con <strong>Radar Docente</strong> para incorporar información oficial del establecimiento donde te desempeñas.
                                    Estos antecedentes <strong>no modifican tus respuestas</strong> — ayudan a <em>contextualizar</em> el análisis con evidencia objetiva y oficial.
                                </p>
                            </div>
                        </div>

                        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                            {[
                                'Índice de Vulnerabilidad Escolar (IVE)',
                                'Dependencia administrativa y sostenedor',
                                'Comuna y región del establecimiento',
                                'Número de docentes del establecimiento',
                                'Distribución por tramos de la Carrera Docente',
                                'Resultados oficiales de aprendizaje por nivel y asignatura',
                                'Indicadores de clima de aprendizaje',
                                'Características del liderazgo escolar',
                                'Otros indicadores oficiales del sistema educacional',
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-2 rounded-lg bg-white/85 border border-teal-100 px-3 py-2 text-xs sm:text-sm text-slate-800">
                                    <span className="text-teal-600 font-bold shrink-0">▸</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Pilares estratégicos */}
            <section className="py-20">
                <div className="mx-auto max-w-6xl px-4 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-rose-600">Cómo funciona</span>
                        <h2 className="mt-3 font-heading text-3xl font-bold text-slate-900 sm:text-4xl">Una capa estratégica sobre tu Portafolio</h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Trabajamos sobre los datos reales del sistema evaluativo chileno. Cada recomendación tiene fundamento en evidencia, no en intuición.
                        </p>
                    </div>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {PILARES_ESTRATEGICOS.map(({ icon: Icon, titulo, texto }) => (
                            <div key={titulo} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="mt-4 font-heading font-semibold text-slate-900">{titulo}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{texto}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Módulos */}
            <section className="border-y border-slate-200 bg-slate-50/40 py-20">
                <div className="mx-auto max-w-5xl px-4 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-rose-600">Cobertura</span>
                        <h2 className="mt-3 font-heading text-3xl font-bold text-slate-900 sm:text-4xl">Los 3 módulos del Portafolio Docente</h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Texto literal de las rúbricas oficiales 2026 por perfil docente (Generalista · Educación Básica · Educación Media).
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 sm:grid-cols-3">
                        {MODULOS.map((m, i) => (
                            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                                    <BookOpen className="h-4 w-4" />
                                </div>
                                <h3 className="mt-4 font-heading text-sm font-bold text-slate-900 leading-snug">{m.titulo}</h3>
                                <p className="mt-2 text-xs text-slate-500">{m.indicadores}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Frase central */}
            <section className="py-20">
                <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
                    <Sparkles className="mx-auto h-8 w-8 text-amber-600" />
                    <p className="mt-4 font-heading text-2xl font-semibold text-slate-900 leading-snug sm:text-3xl">
                        “Un buen Portafolio no es el que busca perfección absoluta,<br />
                        sino el que logra <span className="text-rose-600">coherencia pedagógica</span>, <span className="text-emerald-700">sostenibilidad</span> y <span className="text-sky-700">evidencia sólida</span>.”
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-widest text-slate-500">Portafolio Digital Docente · Acontista Educa</p>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-slate-200 bg-slate-900 py-16 text-white">
                <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
                    <h2 className="font-heading text-3xl font-bold sm:text-4xl">Comienza a estructurar tu estrategia hoy.</h2>
                    <p className="mt-4 text-slate-300 leading-relaxed">
                        Acceso protegido · datos privados · progreso guardado.<br />
                        Crea tu cuenta con Google o correo y empieza con la <strong className="text-rose-300">Ruta Inteligente</strong>.
                    </p>
                    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link to="/register" onClick={() => trackFunnelEvent('register_click', { last_action: 'landing_hero' })}>
                            <Button size="lg" variant="secondary" className="gap-2 text-base bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-register">
                                <Target className="h-4 w-4" />
                                Crear mi cuenta
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button size="lg" variant="ghost" className="text-base text-white hover:bg-slate-800" data-testid="cta-login">
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Ya tengo cuenta
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-slate-200 bg-white">
                <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-4 py-6 text-xs text-slate-500 sm:flex-row lg:px-8">
                    <div>© {new Date().getFullYear()} Portafolio Digital Docente · Acontista Educa. Plataforma estratégica para la Evaluación Docente CPEIP/MINEDUC.</div>
                    <div className="flex items-center gap-3">
                        <span>Integración: Radar Docente</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
